import axios from 'axios';
import { isNativeApp } from './platform';

// Web/PWA uses a relative URL so it works on any domain (custom or preview).
// The bundled native app (Capacitor) has its own local origin, so it MUST call the
// absolute production backend instead of a relative path.
const NATIVE_API_URL = 'https://moneyfeed.io/api';
const API_BASE_URL = isNativeApp() ? NATIVE_API_URL : '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach the JWT Bearer token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401 (expired/invalid session), clear auth and send the user to sign in
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const wasAuthed = !!localStorage.getItem('token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('lastActive');
      if (wasAuthed && !window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  setPin: (data) => api.post('/auth/set-pin', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  getUser: (phoneNumber) => api.get(`/auth/user/${phoneNumber}`),
  updateProfile: (userId, data) => api.put(`/auth/user/${userId}`, data),
  sendOTP: (phoneNumber) => api.post('/auth/send-otp', null, { params: { phone_number: phoneNumber } }),
  verifyOTP: (phoneNumber, otp) => api.post('/auth/verify-otp', null, { params: { phone_number: phoneNumber, otp } }),
  resendOTP: (phoneNumber) => api.post('/auth/resend-otp', null, { params: { phone_number: phoneNumber } }),
  deleteMyAccount: () => api.delete('/account'),
  exportAllData: () => api.get('/account/export', { responseType: 'blob' }),
  webDeleteAccount: (phoneNumber, otp) => api.post('/account/web-delete', { phone_number: phoneNumber, otp }),
  appleChallenge: () => api.post('/auth/apple/challenge'),
  appleLogin: (data) => api.post('/auth/apple', data),
};

export const transactionAPI = {
  getAll: (userId, limit = 100) => api.get('/transactions', { params: { user_id: userId, limit, _t: Date.now() } }),
  create: (userId, data) => api.post(`/transactions?user_id=${userId}`, data),
  delete: (transactionId, userId) => api.delete(`/transactions/${transactionId}`, { params: { user_id: userId } }),
};

export const billAPI = {
  getAll: (userId) => api.get('/bills', { params: { user_id: userId, _t: Date.now() } }),
  create: (userId, data) => api.post(`/bills?user_id=${userId}`, data),
  update: (billId, userId, data) => api.put(`/bills/${billId}`, data, { params: { user_id: userId } }),
  pay: (billId, userId, accountId) => api.post(`/bills/${billId}/pay`, null, { params: { user_id: userId, account_id: accountId } }),
  delete: (billId, userId) => api.delete(`/bills/${billId}`, { params: { user_id: userId } }),
};

export const reportAPI = {
  getSummary: (userId) => api.get('/reports/summary', { params: { user_id: userId, _t: Date.now() } }),
  getLiabilityChart: (userId) => api.get('/dashboard/liability-chart', { params: { user_id: userId } }),
  getTaxSummary: (userId, year) => api.get('/reports/tax-summary', { params: { user_id: userId, year, _t: Date.now() } }),
  getIncomeByClient: (year) => api.get('/reports/income-by-client', { params: year ? { year } : {} }),
  getSubscriptions: () => api.get('/reports/subscriptions'),
};

export const receiptsAPI = {
  list: (params = {}) => api.get('/receipts', { params }),
  summary: () => api.get('/receipts/summary'),
  delete: (id) => api.delete(`/receipts/${id}`),
  exportFile: (params = {}) => api.get('/receipts/export', { params, responseType: 'blob' }),
  exportPdf: (params = {}) => api.get('/receipts/export/pdf', { params, responseType: 'blob' }),
  detail: (txnId) => api.get(`/receipts/txn/${txnId}`),
  edit: (txnId, data) => api.put(`/receipts/txn/${txnId}`, data),
};

export const accountAPI = {
  getAll: (userId) => api.get('/accounts', { params: { user_id: userId } }),
};

export const taxAPI = {
  getPlanner: (year) => api.get('/tax/planner', { params: year ? { year } : {} }),
  updateSettings: (data) => api.put('/tax/planner/settings', data),
  getMileage: () => api.get('/mileage'),
  addMileage: (data) => api.post('/mileage', data),
  deleteMileage: (id) => api.delete(`/mileage/${id}`),
  getRegion: () => api.get('/tax/region'),
  getVatSummary: (year) => api.get('/tax/vat-summary', { params: year ? { year } : {} }),
};

export const onboardingAPI = {
  getStatus: () => api.get('/onboarding/status'),
};

export const companyAPI = {
  getStatements: (year) => api.get('/reports/financial-statements', { params: year ? { year } : {} }),
  updateProfile: (data) => api.put('/company/profile', data),
};

export const goalsAPI = {
  list: () => api.get('/goals'),
  create: (data) => api.post('/goals', data),
  update: (goalId, data) => api.put(`/goals/${goalId}`, data),
  delete: (goalId) => api.delete(`/goals/${goalId}`),
  setCoaching: (enabled) => api.put('/coaching/settings', { enabled }),
  previewCoaching: () => api.post('/coaching/preview'),
};

export const categoryAPI = {
  getAll: () => api.get('/categories'),
};

export const paymentAPI = {
  getPlans: () => api.get('/payments/plans'),
  createCheckout: (planId, userId, originUrl) => api.post('/payments/checkout', { plan_id: planId, user_id: userId, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  openPortal: (returnUrl) => api.post('/payments/portal', { return_url: returnUrl }),
};

export const userAPI = {
  registerPushToken: (token, platform) => api.post('/users/push-token', { token, platform }),
};

export const referralAPI = {
  get: () => api.get('/referral'),
  redeem: (code) => api.post('/referral/redeem', { code }),
};

export const toolsAPI = {
  getRegions: () => api.get('/tools/regions'),
  taxEstimate: (payload) => api.post('/tools/tax-estimate', payload),
  scanDemo: (image_base64, content_type) => api.post('/tools/scan-demo', { image_base64, content_type }),
};

export const invoicesAPI = {
  list: () => api.get('/invoices'),
  get: (id) => api.get(`/invoices/${id}`),
  getPublic: (token) => api.get(`/invoices/public/${token}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  setStatus: (id, status) => api.post(`/invoices/${id}/status`, { status }),
  delete: (id) => api.delete(`/invoices/${id}`),
};

export const businessAPI = {
  scanBatch: (images) => api.post('/business/scan-batch', { images }),
  confirmBatch: (items) => api.post('/business/confirm-batch', { items }),
  exportSummary: () => api.get('/business/export/summary'),
  exportFile: (params) => api.get('/business/export', { params, responseType: 'blob' }),
};

export const teamAPI = {
  getMembers: () => api.get('/team/members'),
  invite: (data) => api.post('/team/invite', data),
  revoke: (inviteId) => api.delete(`/team/invite/${inviteId}`),
};

export const appAPI = {
  getVersion: () => api.get('/app/version'),
};

export const plaidAPI = {
  getStatus: () => api.get('/plaid/status'),
  createLinkToken: () => api.post('/plaid/link-token'),
  exchange: (publicToken, institutionName) => api.post('/plaid/exchange', { public_token: publicToken, institution_name: institutionName }),
  getItems: () => api.get('/plaid/items'),
  sync: (itemId) => api.post(`/plaid/items/${itemId}/sync`),
  remove: (itemId) => api.delete(`/plaid/items/${itemId}`),
};

export const whatsappAPI = {
  sendMessage: (phoneNumber, message) => api.post('/whatsapp/message', { phone_number: phoneNumber, message }),
  getQR: () => api.get('/whatsapp/qr'),
  getConnectionStatus: () => api.get('/whatsapp/connection-status'),
};

export const reviewsAPI = {
  getPublic: () => api.get('/reviews/public'),
  getMine: () => api.get('/reviews/me'),
  submit: (data) => api.post('/reviews', data),
  dismiss: () => api.post('/reviews/dismiss'),
  adminList: (status) => api.get('/admin/reviews', { params: status ? { status } : {} }),
  adminModerate: (id, action) => api.post(`/admin/reviews/${id}/moderate`, { action }),
  adminDelete: (id) => api.delete(`/admin/reviews/${id}`),
};

export const adminAPI = {
  getMetrics: (adminId) => api.get('/admin/metrics', { params: { admin_id: adminId } }),
  getAllUsers: (adminId) => api.get('/admin/users', { params: { admin_id: adminId } }),
  getActivity: (adminId, limit = 50) => api.get('/admin/activity', { params: { admin_id: adminId, limit } }),
  getSubscriptions: (adminId) => api.get('/admin/subscriptions', { params: { admin_id: adminId } }),
  getUserDetails: (userId, adminId) => api.get(`/admin/user/${userId}/details`, { params: { admin_id: adminId } }),
  deleteUser: (userId, adminId) => api.delete(`/admin/user/${userId}`, { params: { admin_id: adminId } }),
  updateUser: (userId, adminId, userData) => api.put(`/admin/user/${userId}`, userData, { params: { admin_id: adminId } }),
  createUser: (adminId, userData) => api.post('/admin/user', userData, { params: { admin_id: adminId } }),
  getWelcomeBroadcastCount: () => api.get('/admin/welcome-broadcast/count'),
  sendWelcomeBroadcast: (payload) => api.post('/admin/welcome-broadcast', payload),
  getInactiveCount: (days = 14) => api.get('/admin/inactive/count', { params: { days } }),
  sendInactiveNudge: (payload) => api.post('/admin/inactive/nudge', payload),
  getAppLaunchStatus: () => api.get('/admin/app-launch/status'),
  sendAppLaunch: (payload) => api.post('/admin/app-launch/broadcast', payload),
  previewRegion: (regionKey) => api.post('/admin/preview-region', { region_key: regionKey }),
  grantTrial: (phone_number, days = 14) => api.post('/admin/trial/grant', { phone_number, days }),
  revokeTrial: (phone_number) => api.post('/admin/trial/revoke', { phone_number }),
};

export const partnersAPI = {
  submitAccountantLead: (data) => api.post('/partners/accountants', data),
};

export default api;